import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import OrgContext from '@totem/components/orgManagement/orgContext';
import {
  Organization,
  OrganizationInput,
} from '@totem/components/orgManagement/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { ORGANIZATION_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

type Props = {
  children?: ReactNode;
};

const OrgContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSending, setIsSending] = useState(false);
  const [data, setData] = useState<Organization[]>([]);
  const [filteredData, setFilteredData] = useState<Organization[]>([]);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [action, setAction] = useState<string>('');
  const [actionItem, setActionItem] = useState<Organization>(null);
  const [input, updateInput] = useState<OrganizationInput>({
    status: ['Active'],
  });

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  useEffect(() => {
    if (isNotNull(data)) {
      let tmpData = data;
      if (
        isNotNull(input) &&
        isNotNull(input.status) &&
        input.status.length > 0 &&
        input.status[0] !== ''
      ) {
        if (input.status[0] === 'Deactivated') {
          tmpData = tmpData.filter((chk) => chk.type === 'DEACTIVATED');
        } else {
          tmpData = tmpData.filter((chk) => chk.type !== 'DEACTIVATED');
        }
      }
      setFilteredData(tmpData);
    }
  }, [data, input]);

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);
      const uri = `${ORGANIZATION_ENDPOINT}`;
      fetch(uri, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: Organization[]) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  const setInput = (updated: Partial<OrganizationInput>) => {
    updateInput((prevInput) => {
      const newInput = { ...prevInput, ...updated };
      return newInput;
    });
  };

  const sendNetSuiteKeyUpdate = useCallback(
    async (keyAction: string, organizationId: string, netSuiteKey: string) => {
      let method = 'GET';
      if (keyAction === 'DELETE') {
        method = 'DELETE';
      }
      if (keyAction === 'ADD') {
        method = 'PUT';
      }

      if (!isSending) {
        fetch(
          `${ORGANIZATION_ENDPOINT}/${organizationId}/customers/${netSuiteKey}`,
          {
            method,
            headers: new Headers({
              Authorization: `Bearer ${getApiToken()}`,
            }),
          },
        ).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            setRefreshData(true);
          }
        });
      }
    }, // End async
    [],
  );

  const updateNetSuiteKey = (
    keyAction: string,
    organizationId: string,
    netSuiteKey: string,
  ) => {
    sendNetSuiteKeyUpdate(keyAction, organizationId, netSuiteKey);
  };

  const handleAction = (newAction: string, newActionItem: Organization) => {
    switch (newAction) {
      case 'refresh':
        setRefreshData(true);
        break;
      default:
        setAction(newAction);

        setActionItem(newActionItem);
    }
  };

  return (
    <OrgContext.Provider
      value={{
        data,
        filteredData,
        loading: isLoading,
        action,
        actionItem,
        onAction: handleAction,
        input,
        setInput,
        onUpdateNetSuiteKey: updateNetSuiteKey,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};
export default OrgContainer;
